define([
	"dojo/_base/lang",
	"underscore",
	"csi",
	"csi/comm/Request",
	"csi/comm/GetRequest",
	"csi/comm/PostRequest",
	"csi/constants",
	"csi/utility/routerUtility",
	"csi/xml"
], function(lang, _, csi, Request, GetRequest, PostRequest, constants, routerUtility, xml) {

	/** time to wait for query accelerator to eventually be consistent (in milliseconds) */
	let _disableQueryAcceleratorTimeout = 10000;

	/** handle for query accelerator timeout */
	let _handleQA = null;

	// String[]: array of unused URLs to be used for new nodes
	var _newUrls = [];

	function _encodeFileName(fileName) {
		//Three special characters produce problems: slash(/), back slash(\) and percentage(%)
		//In case filename has these chars, the encoded request won't get processed.
		//So we encode everything except these chars. Percentage won't work if encoded or not, so will replace it with -

		fileName = fileName.replace(/:/g, "-");
		fileName = fileName.replace(/\</g, "-");
		fileName = fileName.replace(/\>/g, "-");
		fileName = fileName.replace(/\*/g, "-");
		fileName = fileName.replace(/\?/g, "-");
		fileName = fileName.replace(/%/g, "-");
		fileName = fileName.replace(/\+/g, "-");
		// we should remove the last space in the name. Otherwise server can't handle it.
		fileName = fileName.replace(/\s+$/, '');
		fileName = fileName.replace(/\\/g, "-");
		fileName = fileName.replace(/&/g, "-");
		fileName = fileName.replace(/\./g, "-");
		fileName = fileName.replace(/#/g, "-");
		// encode, which will encode rest of the special characters
		fileName = encodeURIComponent(fileName);
		// replace encoded slash with dash.
		return fileName.replace(/%2F/g, "-");
	}

	function _getFileRequest(urlFile, bPreventCache, fileName, isDriveFile = false) {
		var params = {URL: urlFile};
		var request;

		if (bPreventCache) {
			params.tm = csi.getUniqueId();
		}

		if (isDriveFile) {
			request = new GetRequest("DocumentProcessing", "Download", params);
		} else {
			request = new GetRequest("Publisher", "GetDirect", params);
		}

		// add the file name as a last part of the URL path
		// to force browsers show the correct window title with the file name
		// when the user goes to this link directly or use 'Save As...' functionality of the browser.
		// At the time of writing, this trick works for Chrome 49 and Firefox 45
		if (fileName) {
			var urlBase = request.urlBase;
			var endsWithQuestionMark = urlBase.charAt(urlBase.length - 1) == '?';
			if (endsWithQuestionMark)
				urlBase = urlBase.slice(0, -1);

			urlBase += "/" + _encodeFileName(fileName);
			if (endsWithQuestionMark)
				urlBase += '?';
			request.urlBase = urlBase;
		}

		return request;
	}

	/**
	 * Request all nodes necessary to establish location context
	 * @param {cnl} url
	 * @param {cnl} [revisionUrl]
	 * @param {cnl} [merchSeasonUrl]
	 * @return {PostRequest}
	 */
	function getBreadcrumbRequest(url, revisionUrl, merchSeasonUrl) {
		const params = {URL: url};

		if (xml.isValidURL(revisionUrl)) {
			params.URL = revisionUrl;
		}

		if (xml.isValidURL(merchSeasonUrl)) {
			params.MerchSeasonURL = merchSeasonUrl;
		}

		return new PostRequest("SiteAdmin", "BuildBreadcrumb", params).addNodeCrewParameters();
	}

	/**
	 * Return string value to use as an entry in a Map parameter
	 * @param {string|number} key
	 * @param {any} value
	 * @return {string}
	 */
	function getKeyValueParameter(key, value) {
		return key + ":" + value;
	}

	/**
	 * Bypass query accelerator  disableQueryAccelerator is applied only for a specified amount of time, so reset after timeout
	 */
	function bypassQueryAccelerator() {
		if (_disableQueryAcceleratorTimeout) {
			const {statics} = Request.prototype;
			statics.disableQueryAccelerator = true;
			clearTimeout(_handleQA);

			_handleQA = setTimeout(() => {
				statics.disableQueryAccelerator = false;
			}, _disableQueryAcceleratorTimeout);
		}
	}

	/**
	 * Set number of milliseconds to wait for query accelerator to catch up to change requests;
	 * if 0, then query accelerator will not be bypassed
	 * @param {number} milliseconds
	 */
	function setQueryAcceleratorTimeout(milliseconds) {
		_disableQueryAcceleratorTimeout = milliseconds;
	}

	const {
		MODULE,
		MODULE_SEARCH,
		OPERATION,
		OPERATION_QUERY_URL,
		OPERATION_QUERY_XML,
		PARAMETER,
		QRY_URL,
		QRY_XML,
	} = constants;

	return {
		MODULE,
		MOD_SEARCH: MODULE_SEARCH,

		OPERATION,
		OP_URL: OPERATION_QUERY_URL,
		OP_QUERY: OPERATION_QUERY_XML,

		PARAMETER,
		QUERY_URL: QRY_URL,
		QUERY_XML: QRY_XML,

		bypassQueryAccelerator,
		setQueryAcceleratorTimeout,

		getBreadcrumbRequest,
		getKeyValueParameter,

		getNodeRequest: function(/*String|String[]*/url, /*Object?*/params, /*boolean*/bForcePost) {
			// summary: create request to query for one or more urls with given parameters
			if (_.isArray(url)) {
				url = url.filter(xml.isValidURL);

				if (!url.length) {
					return null;
				}

				bForcePost = true;
			} else if (!xml.isValidURL(url)) {
				return null;
			}

			var obj = {"Qry.URL": url};

			if (params) {
				lang.mixin(obj, params);
			}

			if (bForcePost) {
				const request = new PostRequest(MODULE_SEARCH, OPERATION_QUERY_URL, obj);
				request.allowRetry = true;
				return request;
			}

			return new GetRequest(MODULE_SEARCH, OPERATION_QUERY_URL, obj);
		},

		/**
		 * Return request to get urls with associated paths
		 * @param {cnlPath[]} urlPaths
		 * @param {Object} [params]
		 * @return {Request}
		 */
		getUrlPathsRequest(urlPaths, params) {
			const urls = _.uniq(urlPaths.map(([url, path]) => url));
			const paths = _.uniq(urlPaths.map(([url, path]) => path));
			return this.getNodeRequest(urls, params).addDependencyPaths(paths);
		},

		/**
		 * Return attribute name for the given request url
		 * @param {String} requestUrl
		 * @return {String}
		 */
		getNodeFileHrefAttribute(requestUrl) {
			return routerUtility.decomposeHash(requestUrl).Attribute;
		},

		setAsAdmin: function(/*boolean*/isAdmin) {
			Request.prototype.statics.addCrews = !isAdmin;
		},

		getFileHref: function(/*String*/urlFile, /*Boolean*/bPreventCache, /*String*/fileName) {
			// summary: return url string to download file
			if (!urlFile || (urlFile == csi.EMPTY_URL) || (urlFile == csi.EMPTY_FILE_URL)) {
				return null;
			}

			if (urlFile.indexOf(csi.EMPTY_FILE_URL) != 0) {
				return urlFile;
			}
			var href = _getFileRequest(urlFile, bPreventCache, fileName).toRequest();
			return this.getEncodedFileName(href, fileName);
		},

		getCentricDriveFileHref: function(/*String*/cnl, /*Boolean*/bPreventCache, /*String*/fileName) {
			var href = _getFileRequest(cnl, bPreventCache, fileName, true).toRequest();
			return this.getEncodedFileName(href, fileName);
		},

		getEncodedFileName(href, fileName) {
			// add Title as last piece of href so that Edge will display the filename as the title of the window; the trick used in _getFileRequest
			// does not work for Edge; it takes the text after the last slash as the title, and there is always a slash in the file vault URL
			// parameter. So to make sure, add a title to the end of the href with an extra slash. This should not affect other browsers.
			if (fileName) {
				href += "&Title=" + encodeURIComponent("/") + _encodeFileName(fileName);
			}

			return href;
		},

		getFileRequest: function(/*String*/urlFile, /*Boolean*/bPreventCache) {
			// summary: return request to download file from vault
			if (!urlFile || (urlFile == csi.EMPTY_URL) || (urlFile == csi.EMPTY_FILE_URL) || (urlFile.indexOf(csi.EMPTY_FILE_URL) != 0)) {
				return null;
			}

			return _getFileRequest(urlFile, bPreventCache);
		},

		getNodeFileHref: function(/*String*/url, /*String?*/path, /*String*/idAttr, /*Boolean*/bPreventCache, /*Boolean*/bAsDownload) {
			// summary: get href to download file from node attribute along given path based
			if (!xml.isValidURL(url)) {
				return null;
			}

			var params = {
				URL: xml.getUrlPath(url, path),
				Attribute: idAttr,
				Download: bAsDownload
			};

			if (bPreventCache) {
				params.tm = csi.getUniqueId();
			}

			return new GetRequest("Publisher", "GetFromNode", params).toRequest();
		},

		createUrl: function() {
			// summary: return unused url for new node - query for more if empty
			if (!_newUrls.length) {
				var urls = this.createUrls();
				if (urls) {
					_newUrls = urls;
				} else {
					return null;
				}
			}

			return _newUrls.shift();
		},

		createUrls: function(/*Number?*/nCount) {
			var params = {Count: nCount || 20};
			var response = new PostRequest("NodeProcessor", "CreateNodeURL", params).post({sync: true});
			return response.getUrls();
		},

		createFileUrl: function(/*String*/fileName) {
			return new GetRequest("Publisher", "Create", {
				Type: "CS_AUXILARY",
				FN: fileName
			}).get();
		},

		publishFile: function(/*String*/fileName, /*String*/fileUrl, /*String*/content, /*String*/contentType) {
			return new PostRequest("Publisher", "Publish", {FN: fileName, URL: fileUrl})
				.setBinary("FL", content, contentType)
				.post();
		},
	};
});